"use client";

import { useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import posthog from "posthog-js";
import { cn } from "@/lib/utils";

type Provider = "google" | "apple";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M21.35 11.1H12v3.2h5.35c-.24 1.4-1.63 4.1-5.35 4.1-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.05.78 3.75 1.45l2.56-2.46C16.73 4.02 14.63 3 12 3 6.98 3 3 6.98 3 12s3.98 9 9 9c5.2 0 8.64-3.66 8.64-8.81 0-.59-.07-1.04-.29-2.09Z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="18" viewBox="0 0 16 18" aria-hidden="true" fill="currentColor">
      <path d="M13.11 9.58c-.02-2.33 1.9-3.46 1.99-3.52-1.09-1.59-2.77-1.8-3.37-1.83-1.44-.15-2.8.84-3.53.84-.73 0-1.85-.82-3.05-.8-1.57.02-3.01.91-3.81 2.31-1.63 2.82-.42 7 1.17 9.29.78 1.12 1.71 2.38 2.93 2.33 1.18-.05 1.63-.76 3.05-.76 1.42 0 1.82.76 3.06.73 1.27-.02 2.07-1.14 2.84-2.27.9-1.3 1.27-2.57 1.29-2.64-.03-.01-2.46-.94-2.48-3.72M10.7 2.6c.65-.78 1.08-1.87.96-2.95-.93.04-2.06.62-2.72 1.4-.6.7-1.12 1.81-.98 2.87 1.04.08 2.1-.53 2.75-1.32" />
    </svg>
  );
}

export function OAuthButtons({ source = "auth" }: { source?: string }) {
  const t = useTranslations("auth");
  const locale = useLocale();
  const [loading, setLoading] = useState<Provider | null>(null);

  const go = (provider: Provider) => {
    setLoading(provider);
    posthog.capture("sign_in_clicked", { source, provider });
    const next = encodeURIComponent(`/${locale}/new`);
    window.location.href = `/api/auth/oauth/start?provider=${provider}&locale=${locale}&next=${next}`;
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <OAuthCta
        onClick={() => go("google")}
        loading={loading === "google"}
        icon={<GoogleIcon />}
        label={t("common.continueGoogle")}
      />
      <OAuthCta
        onClick={() => go("apple")}
        loading={loading === "apple"}
        icon={<AppleIcon />}
        label={t("common.continueApple")}
      />
    </div>
  );
}

function OAuthCta({
  onClick,
  loading,
  icon,
  label,
}: {
  onClick: () => void;
  loading: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={cn(
        "group inline-flex h-12 items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-[14px] font-medium text-ink transition-all",
        "hover:bg-surface-mute hover:border-line-strong",
        loading && "opacity-70 cursor-wait",
      )}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 rounded-full border-2 border-ink/30 border-t-ink animate-spin" />
      ) : (
        <span className="flex items-center justify-center">{icon}</span>
      )}
      <span className="leading-none">{label}</span>
    </button>
  );
}
